import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, X, ChevronLeft, ChevronRight, Plus, Trash2, Check, ArrowRight, Pencil, User } from 'lucide-react';
import { api } from '../lib/api';

const T1 = '#F1F4F9';
const T2 = 'rgba(200,215,240,0.72)';
const T3 = 'rgba(200,215,240,0.48)';
const BORDER = 'rgba(62,123,250,0.16)';
const STATUS = {
  a_faire: { label: 'À faire', color: '#5BBEFC' },
  en_cours: { label: 'En cours', color: '#F5C56A' },
  a_valider: { label: 'À valider', color: '#A78BFA' },
  fini: { label: 'Fini', color: '#34D399' },
};
const EVENT = {
  created: { label: 'Créée', icon: Plus, color: '#5BBEFC' },
  deleted: { label: 'Supprimée', icon: Trash2, color: '#FF6B6B' },
  done: { label: 'Validée', icon: Check, color: '#34D399' },
  status: { label: 'Statut', icon: ArrowRight, color: '#F5C56A' },
  moved: { label: 'Décalée', icon: ArrowRight, color: '#A78BFA' },
  reassigned: { label: 'Réassignée', icon: User, color: '#5BBEFC' },
  renamed: { label: 'Renommée', icon: Pencil, color: T2 },
};

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shift = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return isoLocal(d); };
const fmtDay = (iso, opts) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', opts);
const hhmm = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

export const TaskHistoryButton = ({ memberById, initialDate }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="task-history-btn"
        onClick={() => setOpen(true)}
        title="Historique des tâches"
        style={{ width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'rgba(62,123,250,0.10)', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', transition: 'background-color 0.14s ease, color 0.14s ease' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = T1; e.currentTarget.style.background = 'rgba(62,123,250,0.2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = T2; e.currentTarget.style.background = 'rgba(62,123,250,0.10)'; }}
      >
        <History size={16} strokeWidth={2.4} />
      </button>
      {open && createPortal(
        <TaskHistoryModal memberById={memberById} initialDate={initialDate || isoLocal(new Date())} onClose={() => setOpen(false)} />,
        document.body,
      )}
    </>
  );
};

function TaskHistoryModal({ memberById, initialDate, onClose }) {
  const [date, setDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    setData(null);
    api.get('/asa-tasks/history', { params: { date } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [date]);

  const names = (ids) => (ids || []).map((id) => memberById?.(id)?.name || id).join(', ');

  return (
    <div data-testid="task-history-modal" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(6,12,26,0.78)', backdropFilter: 'blur(14px)', display: 'grid', placeItems: 'center', padding: 14 }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{ width: '100%', maxWidth: 860, maxHeight: '90vh', display: 'flex', flexDirection: 'column', borderRadius: 22, background: 'linear-gradient(180deg, rgba(20,29,66,0.9) 0%, rgba(14,22,44,0.98) 100%)', border: `1px solid ${BORDER}`, boxShadow: '0 40px 90px -20px rgba(0,0,0,0.75)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px 12px' }}>
          <span style={{ width: 36, height: 36, borderRadius: 11, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg, #5BBEFC, #3E7BFA)', color: '#0B1330' }}><History size={17} strokeWidth={2.6} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: T1 }}>Historique des tâches</div>
            <div data-testid="history-date-label" style={{ fontFamily: 'Sora', fontSize: 12, color: T3, textTransform: 'capitalize' }}>{fmtDay(date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
          </div>
          <button data-testid="history-close" onClick={onClose} style={{ padding: 8, borderRadius: 10, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={15} /></button>
        </div>
        <WeekStrip date={date} setDate={setDate} week={data?.week} />
        <div style={{ overflowY: 'auto', padding: '6px 20px 22px' }}>
          {!data && <div style={{ padding: 30, textAlign: 'center', color: T3, fontFamily: 'Sora', fontSize: 13 }}>Chargement…</div>}
          {data?.error && <div style={{ padding: 30, textAlign: 'center', color: '#FF6B6B', fontFamily: 'Sora', fontSize: 13 }}>Erreur de chargement</div>}
          {data && !data.error && (
            <>
              <Summary s={data.summary} filter={filter} setFilter={setFilter} />
              <PlannedList data={data} filter={filter} names={names} />
              <EventLog events={data.events} names={names} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WeekStrip({ date, setDate, week }) {
  const today = isoLocal(new Date());
  const d = new Date(`${date}T12:00:00`);
  const monday = shift(date, -((d.getDay() + 6) % 7));
  const days = Array.from({ length: 7 }, (_, i) => shift(monday, i));
  const byDate = Object.fromEntries((week || []).map((w) => [w.date, w]));
  const navBtn = (n, Icon, tid) => (
    <button data-testid={tid} onClick={() => setDate(shift(date, n))} style={{ padding: 7, borderRadius: 9, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon size={14} /></button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 20px 12px', borderBottom: `1px solid ${BORDER}` }}>
      {navBtn(-7, ChevronLeft, 'history-prev-week')}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5, minWidth: 0 }}>
        {days.map((iso) => {
          const w = byDate[iso];
          const active = iso === date;
          return (
            <button key={iso} data-testid={`history-day-${iso}`} onClick={() => setDate(iso)} style={{ padding: '7px 2px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${active ? '#3E7BFA' : BORDER}`, background: active ? 'rgba(62,123,250,0.22)' : 'rgba(255,255,255,0.02)', color: active ? T1 : T2, fontFamily: 'Sora', transition: 'background-color 0.14s ease' }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: iso === today ? '#5BBEFC' : T3 }}>{fmtDay(iso, { weekday: 'short' })}</div>
              <div style={{ fontSize: 15, fontWeight: 800, marginTop: 1 }}>{fmtDay(iso, { day: 'numeric' })}</div>
              <div style={{ fontSize: 9.5, fontWeight: 600, color: T3, marginTop: 1 }}>{w ? `${w.done}/${w.planned}` : '·'}</div>
            </button>
          );
        })}
      </div>
      {navBtn(7, ChevronRight, 'history-next-week')}
    </div>
  );
}

function Summary({ s, filter, setFilter }) {
  const items = [
    { id: 'all', label: 'Prévues', value: s.planned, color: '#5BBEFC' },
    { id: 'done', label: 'Faites', value: s.done, color: '#34D399' },
    { id: 'todo', label: 'Pas faites', value: s.planned - s.done, color: '#F5C56A' },
    { id: 'deleted', label: 'Supprimées', value: s.deleted, color: '#FF6B6B' },
    { id: 'events', label: 'Créées / décalées', value: `${s.created} / ${s.moved}`, color: '#A78BFA' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, margin: '14px 0 16px' }}>
      {items.map((it) => (
        <button key={it.id} data-testid={`history-stat-${it.id}`} onClick={() => it.id !== 'events' && setFilter(it.id)} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 12, cursor: it.id === 'events' ? 'default' : 'pointer', background: filter === it.id ? `${it.color}1F` : 'rgba(255,255,255,0.03)', border: `1px solid ${filter === it.id ? `${it.color}66` : BORDER}` }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: it.color }}>{it.value}</div>
          <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: T3 }}>{it.label}</div>
        </button>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: T3, letterSpacing: '0.1em', textTransform: 'uppercase', margin: '6px 0 8px' }}>{children}</div>;
}

function PlannedList({ data, filter, names }) {
  const rows = filter === 'deleted'
    ? data.deleted_planned.map((e) => ({ id: e.task_id, title: e.title, responsable: e.responsable, deleted: e }))
    : data.planned.filter((t) => filter === 'all' || (filter === 'done' ? t.status === 'fini' : t.status !== 'fini'));
  return (
    <div style={{ marginBottom: 18 }}>
      <SectionTitle>Tâches du jour</SectionTitle>
      {rows.length === 0 && <div style={{ padding: '14px 0', color: T3, fontFamily: 'Sora', fontSize: 12.5 }}>Rien ici.</div>}
      {rows.map((t) => {
        const st = t.deleted ? { label: 'Supprimée', color: '#FF6B6B' } : (STATUS[t.status] || STATUS.a_faire);
        return (
          <div key={t.id} data-testid={`history-task-${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', marginBottom: 5, borderRadius: 10, background: 'rgba(255,255,255,0.03)', borderLeft: `3px solid ${st.color}`, opacity: t.status === 'fini' ? 0.6 : 1 }}>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, width: 44, flexShrink: 0 }}>{t.heure_debut || '—'}</span>
            <span style={{ flex: 1, minWidth: 0, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: T1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: t.deleted ? 'line-through' : 'none' }}>{t.title}</span>
            <span style={{ fontFamily: 'Sora', fontSize: 11, color: T3, flexShrink: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{names(t.responsable)}</span>
            <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5, color: st.color, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>{t.deleted ? `${st.label} ${hhmm(t.deleted.at)}` : st.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function EventLog({ events, names }) {
  const detail = (e) => {
    if (e.type === 'status' || e.type === 'done') return `${STATUS[e.from]?.label || e.from} → ${STATUS[e.to]?.label || e.to}`;
    if (e.type === 'moved') return `${e.from} → ${e.to}`;
    if (e.type === 'reassigned') return `${names(e.from)} → ${names(e.to)}`;
    if (e.type === 'renamed') return `« ${e.from} »`;
    if (e.type === 'created' && e.task_date) return `prévue le ${fmtDay(e.task_date, { day: 'numeric', month: 'short' })}`;
    return '';
  };
  return (
    <div>
      <SectionTitle>Ce qui s&apos;est passé ({events.length})</SectionTitle>
      {events.length === 0 && <div style={{ padding: '14px 0', color: T3, fontFamily: 'Sora', fontSize: 12.5 }}>Aucune action enregistrée ce jour-là.</div>}
      {[...events].reverse().map((e) => {
        const m = EVENT[e.type] || EVENT.status;
        const Icon = m.icon;
        return (
          <div key={e.id} data-testid={`history-event-${e.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: `1px dashed ${BORDER}` }}>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, width: 40, flexShrink: 0 }}>{hhmm(e.at)}</span>
            <span style={{ width: 24, height: 24, borderRadius: 8, display: 'grid', placeItems: 'center', background: `${m.color}22`, color: m.color, flexShrink: 0 }}><Icon size={12} strokeWidth={2.6} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: T1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span style={{ color: m.color }}>{m.label}</span> · {e.title}
              </div>
              <div style={{ fontFamily: 'Sora', fontSize: 11, color: T3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                par {e.actor_name || e.actor_id}{detail(e) ? ` · ${detail(e)}` : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
